import React from 'react';
import { motion } from 'framer-motion';
import { Lock, TrendingUp, TrendingDown, MapPin } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';

interface RegionalRatingsProps {
  isPremium: boolean;
  onUpgrade: () => void;
  regionalScores: any;
}

const RegionalRatings: React.FC<RegionalRatingsProps> = ({ isPremium, onUpgrade, regionalScores }) => {
  const getTrend = (score: number) => {
    if (score >= 8.0) return 'up';
    if (score <= 6.0) return 'down';
    return 'neutral';
  };

  const regions = [
    { city: "Los Angeles, USA", score: regionalScores.losAngeles, flag: "🇺🇸", free: true, description: "Beach culture loves your look" },
    { city: "Miami, USA", score: regionalScores.miami, flag: "🇺🇸", free: true, description: "Latin influence boosts appeal" },
    { city: "New York, USA", score: regionalScores.newYork, flag: "🇺🇸", free: true, description: "Diverse preferences" },
    { city: "Chicago, USA", score: regionalScores.chicago, flag: "🇺🇸", free: true, description: "Midwest charm works here" },
    { city: "Dubai, UAE", score: regionalScores.dubai, flag: "🇦🇪", free: true, description: "International appeal" },
    { city: "São Paulo, Brazil", score: regionalScores.saoPaulo, flag: "🇧🇷", free: false, description: "Different beauty standards" },
    { city: "Buenos Aires, Argentina", score: regionalScores.buenosAires, flag: "🇦🇷", free: false, description: "European preferences dominate" },
    { city: "Tokyo, Japan", score: regionalScores.tokyo, flag: "🇯🇵", free: false, description: "Western features valued" },
    { city: "Seoul, South Korea", score: regionalScores.seoul, flag: "🇰🇷", free: false, description: "K-beauty standards differ" },
    { city: "London, UK", score: regionalScores.london, flag: "🇬🇧", free: false, description: "Cosmopolitan tastes" },
    { city: "Paris, France", score: regionalScores.paris, flag: "🇫🇷", free: false, description: "Classic European style" },
    { city: "Barcelona, Spain", score: regionalScores.barcelona, flag: "🇪🇸", free: false, description: "Mediterranean appeal" },
    { city: "Mexico City, Mexico", score: regionalScores.mexicoCity, flag: "🇲🇽", free: false, description: "Top Latin American market" },
    { city: "Sydney, Australia", score: regionalScores.sydney, flag: "🇦🇺", free: false, description: "Beach lifestyle match" },
    { city: "Mumbai, India", score: regionalScores.mumbai, flag: "🇮🇳", free: false, description: "Bollywood standards" },
    { city: "Lagos, Nigeria", score: regionalScores.lagos, flag: "🇳🇬", free: false, description: "Afrocentric preferences" },
  ].map(region => ({ ...region, trend: getTrend(region.score) }));

  const freeRegions = regions.filter(r => r.free);
  const lockedRegions = regions.filter(r => !r.free);

  return (
    <div className="space-y-6">
      {/* Free Preview Section */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <MapPin className="h-5 w-5 text-green-600" />
          <h4 className="font-semibold text-gray-700">Your Top Cities (Free Preview)</h4>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          {freeRegions.map((region, index) => (
            <motion.div
              key={region.city}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              <Card className="p-4 hover:shadow-lg transition-all bg-white">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className="text-2xl">{region.flag}</span>
                    <div>
                      <h4 className="font-semibold text-gray-800">{region.city}</h4>
                      <p className="text-xs text-gray-500">{region.description}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-purple-600">{region.score}</p>
                    <div className="flex items-center gap-1 text-xs">
                      {region.trend === 'up' && (
                        <>
                          <TrendingUp className="h-3 w-3 text-green-600" />
                          <span className="text-green-600 font-semibold">High</span>
                        </>
                      )}
                      {region.trend === 'down' && (
                        <>
                          <TrendingDown className="h-3 w-3 text-red-600" />
                          <span className="text-red-600">Low</span>
                        </>
                      )}
                      {region.trend === 'neutral' && (
                        <span className="text-gray-600">Average</span>
                      )}
                    </div>
                  </div>
                </div>
                <Progress value={region.score * 10} className="h-2" />
              </Card>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Locked Regions Section */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Lock className="h-5 w-5 text-purple-600" />
            <h4 className="font-semibold text-gray-700">
              {lockedRegions.length} More Cities Available
            </h4>
          </div>
          <button
            onClick={onUpgrade}
            className="text-sm font-semibold text-purple-600 hover:text-purple-700 transition-colors"
          >
            Unlock All →
          </button>
        </div>
        
        <div className="grid md:grid-cols-2 gap-4">
          {lockedRegions.slice(0, 6).map((region, index) => (
            <motion.div
              key={region.city}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              <Card 
                className="p-4 relative overflow-hidden transition-all hover:shadow-lg cursor-pointer bg-white"
                onClick={onUpgrade}
              >
                <div className="absolute inset-0 bg-gradient-to-br from-white/95 to-purple-50/95 backdrop-blur-sm z-10 flex items-center justify-center">
                  <div className="text-center">
                    <Lock className="h-6 w-6 text-purple-600 mx-auto mb-1" />
                    <p className="text-xs font-semibold text-purple-600">Premium</p>
                  </div>
                </div>
                
                <div className="blur-[2px]">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className="text-2xl">{region.flag}</span>
                      <div>
                        <h4 className="font-semibold text-gray-800">{region.city}</h4>
                        <p className="text-xs text-gray-500">{region.description}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-purple-600">{region.score}</p>
                      <div className="flex items-center gap-1 text-xs">
                        {region.trend === 'up' && (
                          <>
                            <TrendingUp className="h-3 w-3 text-green-600" />
                            <span className="text-green-600">High</span>
                          </>
                        )}
                        {region.trend === 'down' && (
                          <>
                            <TrendingDown className="h-3 w-3 text-red-600" />
                            <span className="text-red-600">Low</span>
                          </>
                        )}
                        {region.trend === 'neutral' && (
                          <span className="text-gray-600">Average</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <Progress value={region.score * 10} className="h-2" />
                </div>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* Unlock CTA */}
        {!isPremium && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="mt-6"
          >
            <Card 
              className="p-6 bg-gradient-to-r from-purple-500 to-pink-500 text-white cursor-pointer hover:shadow-xl transition-all"
              onClick={onUpgrade}
            >
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-bold text-xl mb-2">
                    🌍 Unlock All {regions.length} Cities Worldwide
                  </h4>
                  <p className="text-purple-100">
                    Discover where you're MOST attractive and optimize your dating strategy
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-3xl font-bold">$9.99</p>
                  <p className="text-sm text-purple-100">One-Time</p>
                </div>
              </div>
            </Card>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default RegionalRatings;